import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { motion } from "framer-motion";
import {
  User, Megaphone, CalendarCheck, TrendingUp, Shield, Globe2, Heart,
  CheckCircle, ChevronRight, Dumbbell, Trophy, Award, Briefcase,
  UserPlus, Star, ArrowRight, Sparkles, Target, Mic
} from "lucide-react";

const fadeUp = { hidden: { opacity: 0, y: 30 }, visible: { opacity: 1, y: 0 } };
const stagger = { visible: { transition: { staggerChildren: 0.1 } } };

const ACCENT = "#D97706";
const ACCENT_HOVER = "#B45309";
const DARK_BG = "#1a0e2e";

export default function ForCoaches() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* ── Navigation ── */}
      <nav className="fixed top-0 w-full z-50 bg-white/95 backdrop-blur border-b border-slate-100">
        <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-8 h-16">
          <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate("/")}>
            <img src="/logo.png" alt="SetPlay" className="h-9 w-9" />
            <span className="font-heading text-xl font-bold uppercase tracking-tight text-court-green">SetPlay</span>
          </div>
          <div className="hidden md:flex items-center gap-6 text-sm font-medium text-slate-600">
            <a href="#why" className="hover:text-court-green transition-colors">Why SetPlay</a>
            <a href="#features" className="hover:text-court-green transition-colors">What You Get</a>
            <a href="#welcome" className="hover:text-court-green transition-colors">Who's Welcome</a>
            <button onClick={() => navigate("/")} className="hover:text-court-green transition-colors">Back to Home</button>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="ghost" onClick={() => navigate("/login")} className="text-sm font-medium" data-testid="nav-login">Login</Button>
            <Button onClick={() => navigate("/login?tab=register")} style={{ backgroundColor: ACCENT }} className="hover:opacity-90 text-white rounded-full px-6 text-sm font-heading uppercase" data-testid="nav-join-coach">
              Join as Coach
            </Button>
          </div>
        </div>
      </nav>

      {/* ═══════════════════════════════════════════════
          HERO
          ═══════════════════════════════════════════════ */}
      <section className="pt-16 relative min-h-[92vh] flex items-center" data-testid="coaches-hero-section">
        <div className="absolute inset-0">
          <img
            src="https://images.unsplash.com/photo-1762025744151-4bd3b166b271?w=1920&q=80"
            alt="Basketball players practicing with coach"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0" style={{ background: `linear-gradient(to bottom, ${DARK_BG}ee, ${DARK_BG}dd, ${DARK_BG}f0)` }} />
        </div>

        <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-8 py-24 text-center">
          <motion.div initial="hidden" animate="visible" variants={stagger}>
            <motion.p variants={fadeUp} className="font-heading uppercase tracking-[0.35em] text-sm mb-5" style={{ color: ACCENT }}>
              For Coaches & Sports Professionals
            </motion.p>
            <motion.h1 variants={fadeUp} className="font-heading uppercase tracking-tighter font-bold text-white text-4xl md:text-6xl lg:text-7xl mb-6 leading-[0.95]" data-testid="hero-heading">
              Mas Visible.<br />Mas Organized.<br /><span style={{ color: ACCENT }}>Mas Kumikita.</span>
            </motion.h1>
            <motion.p variants={fadeUp} className="text-white/75 text-lg md:text-xl max-w-2xl mx-auto mb-4 leading-relaxed">
              Sa SetPlay, puwedeng dalhin ng Coaches at Sports Professionals ang passion nila into a more sustainable digital livelihood (mas malaki and tuloy-tuloy na kita).
            </motion.p>
            <motion.p variants={fadeUp} className="text-white/50 text-base max-w-xl mx-auto mb-10 leading-relaxed">
              Build your profile, promote your services, manage your schedule better, at ma-discover ng mas maraming players, teams, parents, at communities.
            </motion.p>
            <motion.div variants={fadeUp} className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <Button onClick={() => navigate("/login?tab=register")} size="lg"
                style={{ backgroundColor: ACCENT }} className="hover:opacity-90 text-white rounded-full px-10 h-14 text-base font-heading uppercase tracking-wide shadow-lg" data-testid="cta-join-coach">
                <UserPlus className="w-5 h-5 mr-2" />Join as Coach / SP
              </Button>
              <Button onClick={() => navigate("/sp/search")} variant="outline" size="lg"
                className="bg-white/10 backdrop-blur border-white/30 text-white hover:bg-white/20 rounded-full px-10 h-14 text-base font-heading uppercase tracking-wide" data-testid="cta-build-profile">
                <User className="w-5 h-5 mr-2" />Build Your Profile Today
              </Button>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHY SETPLAY FOR COACHES?
          ═══════════════════════════════════════════════ */}
      <section id="why" className="py-20 md:py-32 bg-white" data-testid="why-setplay-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-16 items-start">
            {/* Left: Problem */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
              <motion.p variants={fadeUp} className="font-heading uppercase tracking-[0.2em] text-sm mb-3" style={{ color: ACCENT }}>
                Bakit SetPlay?
              </motion.p>
              <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl mb-6">
                Why SetPlay for<br />Coaches & SPs?
              </motion.h2>
              <motion.p variants={fadeUp} className="text-slate-700 text-base md:text-lg leading-relaxed">
                Hirap palaguin ang income dahil kalat ang bookings sa chat, hirap mag-promote online, walang maayos na profile, at sayang ang oras sa manual scheduling?
              </motion.p>
              <motion.p variants={fadeUp} className="text-slate-500 text-base mt-4 leading-relaxed">
                Sa SetPlay, may platform ka na puwedeng maging digital home ng coaching business mo. Hindi lang ito tungkol sa pagtuturo. Ito ay tungkol sa pagbuo ng mas matatag na kabuhayan habang mas marami kang natutulungang Pilipino.
              </motion.p>
            </motion.div>

            {/* Right: Key capabilities */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="space-y-5">
              <motion.p variants={fadeUp} className="text-sm font-medium text-slate-500 uppercase tracking-wider mb-2">
                Sa SetPlay, mas madali mong:
              </motion.p>
              {[
                { icon: TrendingUp, text: "Kumita nang mas maayos through more booking opportunities" },
                { icon: Sparkles, text: "Maging digital entrepreneur sa sports at fitness space" },
                { icon: CalendarCheck, text: "Ma-organize ang sessions at schedule mo" },
                { icon: Megaphone, text: "Ma-promote ang services at profile mo sa mas professional na paraan" },
                { icon: Heart, text: "Makatulong sa physical at mental health ng mga Pilipino through sports, fitness, and movement" },
              ].map((item, i) => (
                <motion.div key={i} variants={fadeUp} className="flex items-start gap-4 group">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 transition-colors" style={{ backgroundColor: `${ACCENT}10` }}>
                    <item.icon className="w-5 h-5 transition-colors" style={{ color: ACCENT }} strokeWidth={1.5} />
                  </div>
                  <p className="text-slate-700 text-base leading-relaxed pt-2">{item.text}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHAT SETPLAY HELPS YOU DO — BENTO GRID
          ═══════════════════════════════════════════════ */}
      <section id="features" className="py-20 md:py-32 bg-slate-50" data-testid="features-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="font-heading uppercase tracking-[0.2em] text-sm mb-3" style={{ color: ACCENT }}>
              Mga Feature
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl">
              What SetPlay Helps You Do
            </motion.h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-8 lg:grid-cols-12 gap-5 auto-rows-auto">
            {/* Card 1: Profile (Hero Card) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-8 lg:col-span-8 row-span-2 text-white rounded-lg border border-white/10 p-8 md:p-12 flex flex-col justify-between hover:-translate-y-1 transition-transform"
              style={{ backgroundColor: DARK_BG }} data-testid="feature-card-profile">
              <div>
                <User className="w-8 h-8 mb-5" style={{ color: ACCENT }} strokeWidth={1.5} />
                <h3 className="font-heading uppercase tracking-tight font-bold text-2xl md:text-3xl mb-2">Ipakilala ang Sarili Mo Like a Pro</h3>
                <p className="text-white/50 text-sm font-heading uppercase tracking-wider mb-4">Your profile is your first impression.</p>
                <p className="text-white/70 text-base leading-relaxed mb-6 max-w-lg">
                  Sa online world, ang profile mo ang first impression mo. Kapag maayos ang profile mo, mas mataas ang chance na ma-book ka.
                </p>
              </div>
              <div className="space-y-2">
                <p className="text-xs uppercase tracking-widest font-heading" style={{ color: ACCENT }}>Your profile showcases:</p>
                {["Coaching expertise and sport specialization", "Certifications and experience", "Training style and services offered", "Professional presence that builds trust"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: ACCENT }} />
                    <span className="text-white/65 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 2: Promote Services (Tall) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 row-span-2 bg-white rounded-lg border p-8 hover:-translate-y-1 transition-transform" style={{ borderColor: `${ACCENT}30` }} data-testid="feature-card-promote">
              <Megaphone className="w-8 h-8 mb-5" style={{ color: ACCENT }} strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">I-Promote ang Services Mo</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-4">Hindi ka na aasa lang sa referrals.</p>
              <p className="text-slate-600 text-sm leading-relaxed mb-6">
                Mas visible ka online, mas marami kang potential clients.
              </p>
              <div className="space-y-2.5">
                {["Private coaching", "Group sessions & sports clinics", "Fitness classes & team training", "Special workshops & programs"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <CheckCircle className="w-4 h-4 flex-shrink-0" style={{ color: ACCENT }} />
                    <span className="text-slate-600 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 3: Schedule */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-white rounded-lg border border-slate-200 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-schedule">
              <CalendarCheck className="w-8 h-8 text-court-green mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Ayusin ang Schedule Mo</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-3">Less admin stress. More focus sa coaching.</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                No more sabay-sabay na chat, double booking, o kalituhang "Coach, available po ba kayo?" Clients can see when you're available and book directly.
              </p>
            </motion.div>

            {/* Card 4: Earn More (Tall, colored) */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 row-span-2 text-white rounded-lg p-8 hover:-translate-y-1 transition-transform"
              style={{ backgroundColor: ACCENT }} data-testid="feature-card-earn">
              <TrendingUp className="w-8 h-8 text-white mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-xl mb-2">Kumita Nang Mas Malaki</h3>
              <p className="text-white/50 text-sm font-heading uppercase tracking-wider mb-4">From coach to coachpreneur.</p>
              <p className="text-white/80 text-sm leading-relaxed mb-6">
                SetPlay helps you grow your coaching career as a business. Better visibility and scheduling means more earning potential.
              </p>
              <div className="space-y-2.5">
                {["Attract more clients", "Offer more structured programs", "Build repeat bookings", "Level up sideline into sustainable livelihood"].map((t, i) => (
                  <div key={i} className="flex items-center gap-2.5">
                    <ChevronRight className="w-4 h-4 text-white/80 flex-shrink-0" />
                    <span className="text-white/80 text-sm">{t}</span>
                  </div>
                ))}
              </div>
            </motion.div>

            {/* Card 5: Credibility */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-white rounded-lg border border-slate-200 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-credibility">
              <Shield className="w-8 h-8 text-court-green mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Palakasin ang Credibility Mo</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-3">Sa dami ng choices, ang mas mukhang legit, siya ang piliin.</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Players, parents, teams, at communities trust coaches with clear profiles, organized services, and professional presence.
              </p>
            </motion.div>

            {/* Card 6: Reach */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-court-green text-white rounded-lg p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-reach">
              <Globe2 className="w-8 h-8 mb-5" style={{ color: ACCENT }} strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-xl mb-2">Palawakin ang Reach Mo</h3>
              <p className="text-white/50 text-sm font-heading uppercase tracking-wider mb-3">Beyond your circle.</p>
              <p className="text-white/70 text-sm leading-relaxed">
                Ma-discover ka ng players looking for training, parents searching for coaches, teams needing skills development, at communities organizing sports activities.
              </p>
            </motion.div>

            {/* Card 7: Mission */}
            <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
              className="col-span-full md:col-span-4 lg:col-span-4 bg-white rounded-lg border border-slate-200 p-8 hover:-translate-y-1 transition-transform" data-testid="feature-card-mission">
              <Heart className="w-8 h-8 text-red-500 mb-5" strokeWidth={1.5} />
              <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xl mb-2">Maging Bahagi ng Mas Malaking Misyon</h3>
              <p className="text-slate-400 text-sm font-heading uppercase tracking-wider mb-3">Every training session creates impact.</p>
              <p className="text-slate-600 text-sm leading-relaxed">
                Healthier lifestyles, stronger discipline, youth development, better confidence, at more active Filipino communities. Hindi lang negosyo — ambag din ito sa mas malusog na Pilipinas.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          WHO'S WELCOME
          ═══════════════════════════════════════════════ */}
      <section id="welcome" className="py-20 md:py-32 bg-white" data-testid="whos-welcome-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="font-heading uppercase tracking-[0.2em] text-sm mb-3" style={{ color: ACCENT }}>
              Para sa Lahat ng Sports & Fitness Pro
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl mb-4">
              Who's Welcome on SetPlay?
            </motion.h2>
            <motion.p variants={fadeUp} className="text-slate-600 text-base md:text-lg max-w-2xl mx-auto leading-relaxed">
              Kung ang work mo ay tumutulong sa tao na gumalaw, gumaling, lumakas, at maging mas healthy — bagay ka sa SetPlay.
            </motion.p>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {[
              { icon: Trophy, title: "Sports Coaches" },
              { icon: Target, title: "Skills Trainers" },
              { icon: Dumbbell, title: "Personal Trainers" },
              { icon: Sparkles, title: "Fitness Instructors" },
              { icon: Star, title: "Yoga Teachers" },
              { icon: Mic, title: "Dance Coaches" },
              { icon: Award, title: "Martial Arts Instructors" },
              { icon: TrendingUp, title: "Strength & Conditioning" },
              { icon: Briefcase, title: "Team Coaches" },
              { icon: Shield, title: "Referees & Facilitators" },
              { icon: Heart, title: "Wellness Professionals" },
              { icon: Globe2, title: "Movement Specialists" },
            ].map((type, i) => (
              <motion.div key={i} variants={fadeUp}
                className="text-center p-5 rounded-lg border border-slate-200 hover:-translate-y-1 transition-transform group" data-testid={`coach-type-${i}`}>
                <div className="w-12 h-12 rounded-xl flex items-center justify-center mx-auto mb-3 group-hover:scale-110 transition-transform" style={{ backgroundColor: `${ACCENT}10` }}>
                  <type.icon className="w-6 h-6" style={{ color: ACCENT }} strokeWidth={1.5} />
                </div>
                <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-xs">{type.title}</h3>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          KEY BENEFITS
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-32 bg-slate-50" data-testid="benefits-section">
        <div className="max-w-6xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger} className="text-center mb-14">
            <motion.p variants={fadeUp} className="font-heading uppercase tracking-[0.2em] text-sm mb-3" style={{ color: ACCENT }}>Key Benefits</motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-court-green text-3xl md:text-5xl">
              Bakit Nasa SetPlay ang Mga Coach?
            </motion.h2>
          </motion.div>

          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 md:gap-12">
            {[
              { icon: TrendingUp, title: "Mas Maraming Earning Opportunities", desc: "More visibility means more chances to be booked and earn." },
              { icon: CalendarCheck, title: "Mas Organized na Schedule", desc: "Iwas kalat, iwas conflict, mas smooth ang operations mo." },
              { icon: Globe2, title: "Mas Malakas na Online Presence", desc: "You look more credible, professional, and ready for clients." },
              { icon: Megaphone, title: "Mas Madaling I-Market", desc: "Hindi ka lang magaling — mas madali ka pang mahanap." },
              { icon: Heart, title: "Mas Malaking Impact sa Community", desc: "Habang lumalaki ang career mo, mas marami ka ring natutulungang Pilipino." },
            ].map((b, i) => (
              <motion.div key={i} variants={fadeUp} className="group">
                <b.icon className="w-8 h-8 mb-4 group-hover:scale-110 transition-transform" style={{ color: ACCENT }} strokeWidth={1.5} />
                <h3 className="font-heading uppercase tracking-tight font-bold text-court-green text-lg mb-2">{b.title}</h3>
                <p className="text-slate-600 text-sm leading-relaxed">{b.desc}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          CLOSING MANTRA
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-32" style={{ backgroundColor: DARK_BG }} data-testid="mantra-section">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={stagger}>
            <motion.p variants={fadeUp} className="font-heading uppercase tracking-[0.2em] text-sm mb-6" style={{ color: ACCENT }}>
              Galingan. Palaguin. Palakasin.
            </motion.p>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-white text-2xl md:text-4xl lg:text-5xl mb-4 leading-tight">
              Galingan mo ang coaching mo.
            </motion.h2>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-white/70 text-2xl md:text-4xl lg:text-5xl mb-4 leading-tight">
              Palaguin mo ang kabuhayan mo.
            </motion.h2>
            <motion.h2 variants={fadeUp} className="font-heading uppercase tracking-tight font-bold text-2xl md:text-4xl lg:text-5xl leading-tight" style={{ color: ACCENT }}>
              I-improve mo ang Fitness health ng Pilipinas.
            </motion.h2>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          CTA BLOCK
          ═══════════════════════════════════════════════ */}
      <section className="py-20 md:py-28 bg-white" data-testid="cta-block">
        <div className="max-w-4xl mx-auto px-6 md:px-12">
          <motion.div initial="hidden" whileInView="visible" viewport={{ once: true }} variants={fadeUp}
            className="text-white rounded-[32px] p-10 md:p-16 text-center shadow-2xl"
            style={{ backgroundColor: ACCENT }}>
            <h2 className="font-heading uppercase tracking-tight font-bold text-3xl md:text-4xl mb-4">
              Ready Ka Na Bang Palaguin ang Coaching Career Mo?
            </h2>
            <p className="text-white/80 text-base md:text-lg mb-8 max-w-xl mx-auto leading-relaxed">
              Join SetPlay and build your professional profile, promote your services, organize your schedule, at i-level up ang coaching mo into a stronger digital livelihood.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center flex-wrap">
              <Button onClick={() => navigate("/login?tab=register")} size="lg"
                className="bg-white hover:bg-slate-100 rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" style={{ color: ACCENT }} data-testid="cta-bottom-join">
                <UserPlus className="w-5 h-5 mr-2" />Join as Coach / SP
              </Button>
              <Button onClick={() => navigate("/sp/search")} variant="outline" size="lg"
                className="border-white/40 text-white hover:bg-white/10 rounded-full px-10 h-13 text-base font-heading uppercase tracking-wide" data-testid="cta-bottom-profile">
                <User className="w-5 h-5 mr-2" />Build Your Profile Today
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════
          FOOTER
          ═══════════════════════════════════════════════ */}
      <footer className="py-12 bg-[#0a2a1e] text-white/60" data-testid="footer">
        <div className="max-w-6xl mx-auto px-4 sm:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 mb-10">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <img src="/logo.png" alt="SetPlay" className="h-8 w-8" />
                <span className="font-heading text-lg font-bold text-white uppercase">SetPlay</span>
              </div>
              <p className="text-sm leading-relaxed">Connecting Filipino communities through sports. One game at a time.</p>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Platform</h4>
              <div className="space-y-2 text-sm">
                <a href="/#how-it-works" className="block hover:text-white transition-colors">How It Works</a>
                <a href="/search" className="block hover:text-white transition-colors">Find Courts</a>
                <a href="/sessions" className="block hover:text-white transition-colors">Browse Sessions</a>
                <a href="/events" className="block hover:text-white transition-colors">Events</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Join Us</h4>
              <div className="space-y-2 text-sm">
                <a href="/for-players" className="block hover:text-white transition-colors">For Players</a>
                <a href="/for-venues" className="block hover:text-white transition-colors">For Venues</a>
                <a href="/for-coaches" className="block hover:text-white transition-colors font-medium" style={{ color: ACCENT }}>For Coaches / SP</a>
                <a href="/sp/search" className="block hover:text-white transition-colors">Find a Coach</a>
              </div>
            </div>
            <div>
              <h4 className="font-heading text-sm text-white uppercase tracking-wider mb-3">Contact</h4>
              <div className="space-y-2 text-sm">
                <p>gamena@setplay.net</p>
                <p>Metro Manila, Philippines</p>
              </div>
            </div>
          </div>
          <div className="border-t border-white/10 pt-6 flex flex-col sm:flex-row items-center justify-between gap-3">
            <p className="text-sm">Proudly Built for the Philippines by <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-white hover:text-[#FBBF24] underline underline-offset-2 transition-colors">SusTek Labs</a></p>
            <div className="flex gap-4 text-sm">
              <a href="#" className="hover:text-white">Privacy</a>
              <a href="#" className="hover:text-white">Terms</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
